import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';  // Import useLocation for getting query string
import '../App.css';
import { handleUserFlow } from '../userFlow';  // Adjust path to the correct location
import { handleUserFlowPayLater } from '../userFlowPayLater';  // Adjust path to the correct location
import axios from 'axios';
import { createSubscription, createSubscriptionPayLater, fetchOptions, getConfirmationDetails, resendConfirmationOTP} from '../apiService';
import UserConfirmation from '../Components/UserConfirmation';
import apiClient from '../apiClient';
import { useGlobalError } from '../Components/GlobalErrorContext';
import Select from 'react-select';

type agent = {
  id: number;
  name: string;
};

type Complex = {
  id: number;
  name: string;
};

type Suburb = {
  id: number;
  name: string;
};

// Turns any API error shape into a readable "Field: message" string.
// Handles:
//  - FastAPI/Pydantic-style validation errors: { detail: [{ loc: [...], msg, type }] }
//  - Single-field error objects: { detail: { field, message } }
//  - Plain string details: { detail: "some message" }
//  - Generic fallbacks: { message } or Error.message
const formatApiError = (error: any): string => {
  const detail = error?.response?.data?.detail;

  // FastAPI/Pydantic-style validation errors: detail is an array
  if (Array.isArray(detail)) {
    return detail
      .map((err: any) => {
        // loc is usually like ["body", "identification_reference"]
        const field = Array.isArray(err.loc) ? err.loc[err.loc.length - 1] : 'Field';
        const fieldLabel = String(field)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c: string) => c.toUpperCase());
        return `${fieldLabel}: ${err.msg}`;
      })
      .join('\n');
  }

  // Single-field error object: { field: "identification_reference", message: "..." }
  if (detail && typeof detail === 'object' && (detail.field || detail.message)) {
    const fieldLabel = detail.field
      ? String(detail.field).replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
      : 'Field';
    return `${fieldLabel}: ${detail.message ?? 'Invalid value'}`;
  }

  // Plain string detail
  if (typeof detail === 'string') return detail;

  return (
    error?.response?.data?.message ||
    error?.message ||
    'Something went wrong. Please try again.'
  );
};

const ApplicationForm = () => {

  const [formData, setFormData] = useState({
    sales_agent: '',
    first_name: '',
    last_name: '',
    identification_type: 'rsaid',
    identification_reference: '',
    complex_building: '',
    suburb: '',
    unit_number: '',
    street_address: '',
    province: '',
    postal_code: '',
    postal_address: '',
    email: '',
    mobile_number: '',
    confirmMobileNumber: '',
    alternate_contact_number: '',
    password: '',
    confirmPassword: '',
    identityFile: null,
    addressFile: null,
    termsAccepted: false,
    preferred_payment_method: 'creditcard', // Default to "creditcard" per schema
    subscription_initialisation_type: "onceoff_and_recurring",
    billing_auto_renew: true,
    application_type: "manual",
    tokenize_card: false,
  });

  const [packageDetails, setPackageDetails] = useState({
    speed: '',
    price: '',
    activationFee: '',
    monthlyPayment: '',
    totalPayment: '',
    id: '',
    productType: '',
    name: '',
    recurringDescription: '',
    onceoffDescription: '',
    currency: '',
    renewalType: '',
    durationDays: '',
  });

  // States for storing dropdown options
  const [salesAgents, setSalesAgents] = useState<agent[]>([]); 
  const [complexes, setComplexes] = useState<Complex[]>([]);
  const [suburbs, setSuburbs] = useState<Suburb[]>([]);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false); // Modal visibility state
  const [modalSubmitHandler, setModalSubmitHandler] = useState<((details: any) => void) | null>(null);

  // Error modal state: shows a popup listing which field(s) failed validation
  const [errorModal, setErrorModal] = useState<{ show: boolean; title: string; errors: string[] }>({
    show: false,
    title: 'Fix the Following',
    errors: [],
  });

  const closeErrorModal = () => setErrorModal({ show: false, title: 'Please Fix the Following', errors: [] });

  // Shows the error modal from a client-side validation errors object ({ field: message })
  const showValidationErrors = (errors: Record<string, string>) => {
    setErrorModal({
      show: true,
      title: 'Please Fix the Following',
      errors: Object.values(errors),
    });
  };

  // Shows the error modal from a server/API error (formatApiError returns "Field: message" lines)
  const showApiError = (error: any) => {
    const formatted = formatApiError(error);
    setErrorModal({
      show: true,
      title: 'There Was a Problem With Your Submission',
      errors: formatted
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    });
  };

  // const [modalSubmitHandler, setModalSubmitHandler] = useState(null);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const userDetails = {};
  const subscriptionDetails = {};
  const [showPassword, setShowPassword] = useState(false); 
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // Toggle for Confirm Password field
  const { showErrorModal } = useGlobalError();

  const [payLaterClicked, setPayLaterClicked] = useState(false);

  const getConfirmationFromModal = (confirmationDetails: any) => {
    return new Promise((resolve) => {
      setModalSubmitHandler(() => resolve); // Store resolve to call from modal
      setConfirmationDetails(confirmationDetails);
      setIsModalVisible(true); // Show the modal
    });
  };


  // Use location to get query string
  const location = useLocation();

  useEffect(() => {

    // Fetch options from the API
    const fetchData = async () => {
      const suburbsData = await fetchOptions('suburb');
      setSuburbs(suburbsData);

      const salesAgents = await fetchOptions('agent');
      setSalesAgents(salesAgents);

      const complexesData = await fetchOptions('complex');
      setComplexes(complexesData);
    };

    fetchData();

    // Get the encoded details from the URL
    const urlParams = new URLSearchParams(location.search);
    const encodedDetails = urlParams.get('details');

    if (encodedDetails) {
      // Decode and parse the plan details
      const plan = JSON.parse(decodeURIComponent(encodedDetails));
      setPackageDetails({
        speed: plan.speed,
        price: plan.price,
        activationFee: plan.activationFee,
        monthlyPayment: plan.monthlyPayment,
        totalPayment: plan.totalPayment,

        // Include all product details even if not displayed
        id: plan.id,
        productType: plan.product_type,
        name: plan.name,
        recurringDescription: plan.recurring_description,
        onceoffDescription: plan.onceoff_description,
        currency: plan.currency,
        renewalType: plan.renewal_type,
        durationDays: plan.duration_days,
      });
    }
  }, [location]);



  const handleChange = (e: any) => {
    const { name, value, type, checked, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'file' ? files[0] : type === 'checkbox' ? checked : value,
    });
  };

const payLaterRef = useRef(false);

// Refs for form fields to enable scrolling to invalid fields
const fieldRefs = {
  sales_agent: useRef<HTMLSelectElement>(null),
  first_name: useRef<HTMLInputElement>(null),
  last_name: useRef<HTMLInputElement>(null),
  identification_reference: useRef<HTMLInputElement>(null),
  complex_building: useRef<HTMLDivElement>(null),
  suburb: useRef<HTMLSelectElement>(null),
  unit_number: useRef<HTMLInputElement>(null),
  street_address: useRef<HTMLInputElement>(null),
  mobile_number: useRef<HTMLInputElement>(null),
  confirmMobileNumber: useRef<HTMLInputElement>(null),
  password: useRef<HTMLInputElement>(null),
  confirmPassword: useRef<HTMLInputElement>(null),
  termsAccepted: useRef<HTMLInputElement>(null),
};

// Comprehensive form validation function
const validateForm = (): { isValid: boolean; firstInvalidField: string | null; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};
  let firstInvalidField: string | null = null;

  // Define required fields
  const requiredFields = [
    { name: 'sales_agent', label: 'Sales Agent' },
    { name: 'first_name', label: 'First Name' },
    { name: 'last_name', label: 'Last Name' },
    { name: 'identification_reference', label: 'Identity/Passport Number' },
    { name: 'complex_building', label: 'Complex/House/Business' },
    { name: 'suburb', label: 'Suburb' },
    { name: 'unit_number', label: 'Unit Number' },
    { name: 'street_address', label: 'Street Address' },
    { name: 'mobile_number', label: 'Mobile Number' },
    { name: 'confirmMobileNumber', label: 'Confirm Mobile Number' },
    { name: 'password', label: 'Password' },
    { name: 'confirmPassword', label: 'Confirm Password' },
  ];

  // Check required fields
  requiredFields.forEach((field: any) => {
    const value = formData[field.name as keyof typeof formData];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors[field.name] = `${field.label} is required`;
      if (!firstInvalidField) firstInvalidField = field.name;
    }
  });

  // Validate mobile numbers match
  if (formData.mobile_number && formData.confirmMobileNumber && formData.mobile_number !== formData.confirmMobileNumber) {
    errors.confirmMobileNumber = 'Mobile numbers do not match';
    if (!firstInvalidField) firstInvalidField = 'confirmMobileNumber';
  }

  // Validate passwords match
  if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
    if (!firstInvalidField) firstInvalidField = 'confirmPassword';
  }

  // Validate password strength (at least 6 characters)
  if (formData.password && formData.password.length < 6) {
    errors.password = 'Password must be at least 6 characters';
    if (!firstInvalidField) firstInvalidField = 'password';
  }

  // Validate terms acceptance
  if (!formData.termsAccepted) {
    errors.termsAccepted = 'You must accept the Terms & Conditions';
    if (!firstInvalidField) firstInvalidField = 'termsAccepted';
  }

  // Validate email if provided
  if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
    errors.email = 'Please enter a valid email address';
    if (!firstInvalidField) firstInvalidField = 'email';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    firstInvalidField,
    errors,
  };
};

// Scroll to first invalid field
const scrollToField = (fieldName: string | null) => {
  if (!fieldName || !fieldRefs[fieldName as keyof typeof fieldRefs]) return;
  
  const ref = fieldRefs[fieldName as keyof typeof fieldRefs];
  if (ref.current) {
    // Scroll into view with smooth behavior
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Focus on the field if it's focusable
    if (ref.current.focus) {
      setTimeout(() => ref.current?.focus(), 300);
    }
  }
};

const handleOrderNowClick = (e: any) => {
  handleSubmit(e, false); // Order Now → payLater = false
};

const handlePayLaterClick = (e: any) => {
  handleSubmit(e, true); // Pay Later → payLater = true
};

const handleSubmit = async (e: React.FormEvent<HTMLFormElement>, payLater = false) => {
  e.preventDefault();
  setIsFormSubmitted(true);

  // Validate form
  const { isValid, firstInvalidField, errors } = validateForm();
  
  if (!isValid) {
    // Show the error modal listing exactly which field(s) are invalid
    showValidationErrors(errors);

    // Scroll to the first invalid field
    scrollToField(firstInvalidField);
    return;
  }

  try {
    // Step 1: Register user & show OTP modal
    const { updatedConfirmationDetails, registeredUser } = await (
      payLater
        ? handleUserFlowPayLater(formData, packageDetails, setIsModalVisible, setConfirmationDetails, getConfirmationFromModal)
        : handleUserFlow(formData, packageDetails, setIsModalVisible, setConfirmationDetails, getConfirmationFromModal)
    );

    // Step 2: After OTP is confirmed, create subscription
    const subscriptionDetails = {
      portal_end_customer_id: registeredUser.id,
      portal_product_id: packageDetails.id,
      preferred_payment_method: formData.preferred_payment_method,
      total_cost: packageDetails.totalPayment,
      activation_fee: packageDetails.activationFee,
      monthly_cost: packageDetails.monthlyPayment,
      duration_days: packageDetails.durationDays,
      speed: packageDetails.speed,
      product_name: packageDetails.name,
    };

    if (payLater) {
      await createSubscriptionPayLater(subscriptionDetails);
      window.location.href = '/pay-later';
    } else {
      const subscription = await createSubscription(subscriptionDetails);
      if (subscription.paymentRedirect) window.location.href = subscription.paymentRedirect;
    }

  } catch (error: any) {
    console.error('Submission error:', error);
    // Shows exactly which field(s) failed server-side validation, e.g.
    // "Identification Reference: ensure this value has at least 13 characters"
    showApiError(error);
  }
};









  return (
    <>
    
    <form onSubmit={handleSubmit} className="application-form container" id="application-form-page">
      <h2 className="mb-4" style={{ textAlign: 'left' }}>{packageDetails.speed} PAYMENT PLAN</h2>

      {/* Personal & Address Information Section */}
      <div className="form-section">
        <br />
        {/* Sales Representative */}
        <label>Select FTTH Sales Representative that assisted you:</label>
        <select 
          ref={fieldRefs.sales_agent}
          name="sales_agent" 
          onChange={handleChange} 
          required
          className={isFormSubmitted && !formData.sales_agent ? 'is-invalid form-control' : 'form-control'}
          style={isFormSubmitted && !formData.sales_agent ? { borderColor: '#dc3545' } : {}}
        >
          <option value="">Select an agent</option>
          {salesAgents.map(agent => (
            <option key={agent.id} value={agent.name}>{agent.name}</option>
          ))}
        </select>
        {isFormSubmitted && !formData.sales_agent && (
          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Sales Agent is required</div>
        )}

        {/* Name Section */}
        <div className="row">
          <div className="col-md-6">
            <label>First Name*</label>
            <input 
              ref={fieldRefs.first_name}
              type="text" 
              name="first_name" 
              onChange={handleChange}
              className={isFormSubmitted && !formData.first_name ? 'form-control is-invalid' : 'form-control'}
              style={isFormSubmitted && !formData.first_name ? { borderColor: '#dc3545' } : {}}
              required
            />
            {isFormSubmitted && !formData.first_name && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>First Name is required</div>
            )}
          </div>
          <div className="col-md-6">
            <label>Last Name*</label>
            <input 
              ref={fieldRefs.last_name}
              type="text" 
              name="last_name" 
              onChange={handleChange}
              className={isFormSubmitted && !formData.last_name ? 'form-control is-invalid' : 'form-control'}
              style={isFormSubmitted && !formData.last_name ? { borderColor: '#dc3545' } : {}}
              required 
            />
            {isFormSubmitted && !formData.last_name && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Last Name is required</div>
            )}
          </div>
        </div>

        {/* ID Section */}
        <div className="d-flex align-items-center">
          <label>
            <input
              type="radio"
              name="identification_type"
              value="rsaid"
              checked={formData.identification_type === 'rsaid'}
              onChange={handleChange} />
            RSA ID
          </label>
          <label className="ms-3">
            <input
              type="radio"
              name="identification_type"
              value="passport"
              onChange={handleChange} />
            Passport (Non SA)
          </label>
        </div>
        <label>Identity Number (SA) / Passport Number (NON-SA)*</label>
        <input 
          ref={fieldRefs.identification_reference}
          type="text" 
          name="identification_reference" 
          onChange={handleChange}
          className={isFormSubmitted && !formData.identification_reference ? 'form-control is-invalid' : 'form-control'}
          style={isFormSubmitted && !formData.identification_reference ? { borderColor: '#dc3545' } : {}}
          required 
        />
        {isFormSubmitted && !formData.identification_reference && (
          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Identity/Passport Number is required</div>
        )}

        {/* Address Section */}
        <div className="row">
          <div className="col-md-6">
            <label>Complex/House/Business*</label>
            <div 
              ref={fieldRefs.complex_building}
              style={isFormSubmitted && !formData.complex_building ? { borderRadius: '4px', boxShadow: 'inset 0 0 0 2px #dc3545' } : {}}
            >
              <Select
                name="complex_building"
                options={complexes.map((complex) => ({
                  value: complex.name,
                  label: complex.name,
                }))}
                onChange={(selectedOption) =>
                  handleChange({
                    target: { name: 'complex_building', value: selectedOption?.value || '' },
                  })
                }
                placeholder="Search or select a complex"
                isSearchable
                required
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: isFormSubmitted && !formData.complex_building ? '#dc3545' : state.isFocused ? '#e56521' : base.borderColor,
                    boxShadow: state.isFocused ? '0 0 0 1px #e56521' : isFormSubmitted && !formData.complex_building ? '0 0 0 1px #dc3545' : base.boxShadow,
                    '&:hover': {
                      borderColor: isFormSubmitted && !formData.complex_building ? '#dc3545' : '#e56521',
                    },
                  }),
                }}
              />
            </div>
            {isFormSubmitted && !formData.complex_building && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Complex/House/Business is required</div>
            )}
          </div>
          <div className="col-md-6">
            <label>Suburb*</label>
            <select 
              ref={fieldRefs.suburb}
              name="suburb" 
              onChange={handleChange}
              className={isFormSubmitted && !formData.suburb ? 'form-control is-invalid' : 'form-control'}
              style={isFormSubmitted && !formData.suburb ? { borderColor: '#dc3545' } : {}}
              required
            >
              <option value="">Select a suburb</option>
              {suburbs.map((suburb) => (
                <option key={suburb.id} value={suburb.name}>
                  {suburb.name}
                </option>
              ))}
            </select>
            {isFormSubmitted && !formData.suburb && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Suburb is required</div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <label>Unit Number*</label>
            <input 
              ref={fieldRefs.unit_number}
              type="text" 
              name="unit_number" 
              onChange={handleChange}
              className={isFormSubmitted && !formData.unit_number ? 'form-control is-invalid' : 'form-control'}
              style={isFormSubmitted && !formData.unit_number ? { borderColor: '#dc3545' } : {}}
              required 
            />
            {isFormSubmitted && !formData.unit_number && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Unit Number is required</div>
            )}
          </div>
          <div className="col-md-6">
            <label>Street Address*</label>
            <input 
              ref={fieldRefs.street_address}
              type="text" 
              name="street_address" 
              onChange={handleChange}
              className={isFormSubmitted && !formData.street_address ? 'form-control is-invalid' : 'form-control'}
              style={isFormSubmitted && !formData.street_address ? { borderColor: '#dc3545' } : {}}
              required 
            />
            {isFormSubmitted && !formData.street_address && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Street Address is required</div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <label>Province</label>
            <input type="text" name="province" onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label>ZIP / Postal Code</label>
            <input type="text" name="postal_code" onChange={handleChange} />
          </div>
        </div>

        <label>Postal Address (Optional)</label>
        <textarea name="postal_address" rows={2} onChange={handleChange}></textarea>

        <div className="row">
          <div className="col-md-6">
            <label>Mobile Number*</label>
            <input 
              ref={fieldRefs.mobile_number}
              type="tel" 
              name="mobile_number" 
              onChange={handleChange}
              className={isFormSubmitted && !formData.mobile_number ? 'form-control is-invalid' : 'form-control'}
              style={isFormSubmitted && !formData.mobile_number ? { borderColor: '#dc3545' } : {}}
              required 
            />
            {isFormSubmitted && !formData.mobile_number && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Mobile Number is required</div>
            )}
          </div>
          <div className="col-md-6">
            <label>Confirm Mobile Number*</label>
            <input 
              ref={fieldRefs.confirmMobileNumber}
              type="tel" 
              name="confirmMobileNumber" 
              onChange={handleChange}
              className={isFormSubmitted && (!formData.confirmMobileNumber || (formData.mobile_number && formData.mobile_number !== formData.confirmMobileNumber)) ? 'form-control is-invalid' : 'form-control'}
              style={isFormSubmitted && (!formData.confirmMobileNumber || (formData.mobile_number && formData.mobile_number !== formData.confirmMobileNumber)) ? { borderColor: '#dc3545' } : {}}
              required 
            />
            {isFormSubmitted && !formData.confirmMobileNumber && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Confirm Mobile Number is required</div>
            )}
            {isFormSubmitted && formData.confirmMobileNumber && formData.mobile_number && formData.mobile_number !== formData.confirmMobileNumber && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Mobile numbers do not match</div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <label>Email</label>
            <input type="email" name="email" onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label>Alternate Number</label>
            <input type="tel" name="alternate_contact_number" onChange={handleChange} />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <label>Create Password*</label>
            <div className="input-group">
              <input
                ref={fieldRefs.password}
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className={isFormSubmitted && (!formData.password || formData.password.length < 6 || formData.password !== formData.confirmPassword) ? 'form-control is-invalid' : 'form-control'}
                style={{ width: '80%', boxShadow: 'none', borderColor: isFormSubmitted && (!formData.password || formData.password.length < 6 || formData.password !== formData.confirmPassword) ? '#dc3545' : undefined }}
              />
              <span className="input-group-text" onClick={() => setShowPassword(!showPassword)} style={{ cursor: 'pointer', height: '42px' }}>
                {showPassword ? (
                  <i className="bi bi-eye-slash"></i>
                ) : (
                  <i className="bi bi-eye"></i>
                )}
              </span>
            </div>
            {isFormSubmitted && !formData.password && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Password is required</div>
            )}
            {isFormSubmitted && formData.password && formData.password.length < 6 && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Password must be at least 6 characters</div>
            )}
            {isFormSubmitted && formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Passwords do not match</div>
            )}
          </div>
          <div className="col-md-6">
            <label>Confirm Password*</label>
            <div className="input-group">
              <input
                ref={fieldRefs.confirmPassword}
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className={isFormSubmitted && (!formData.confirmPassword || formData.password !== formData.confirmPassword) ? 'form-control is-invalid' : 'form-control'}
                style={{ width: '80%', boxShadow: 'none', borderColor: isFormSubmitted && (!formData.confirmPassword || formData.password !== formData.confirmPassword) ? '#dc3545' : undefined }}
              />
              <span className="input-group-text" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ cursor: 'pointer', height: '42px' }}>
                {showConfirmPassword ? (
                  <i className="bi bi-eye-slash"></i>
                ) : (
                  <i className="bi bi-eye"></i>
                )}
              </span>
            </div>
            {isFormSubmitted && !formData.confirmPassword && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Confirm Password is required</div>
            )}
            {isFormSubmitted && formData.confirmPassword && formData.password && formData.password !== formData.confirmPassword && (
              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>Passwords do not match</div>
            )}
          </div>
        </div>
      </div>

      {/* RICA Documents Section */}
      <div className="form-section" >
        <br />
        <h4 style={{ margin: 0}}>RICA DOCUMENTS</h4>
        <hr />
        <div className="row">
          <div className="col-md-6">
            <label>Identity or Passport</label>
            <input type="file" name="identityFile" onChange={handleChange} style={{ backgroundColor: '#e56521'}} />
          </div>
          <div className="col-md-6">
            <label>Proof of Residential Address</label>
            <input type="file" name="addressFile" onChange={handleChange} style={{ backgroundColor: '#e56521' }} />
          </div>
        </div>
        <hr />
      </div>

      {/* Terms and Conditions */}
      <div className="form-section">
        <br />
        <h4 style={{ margin: 0 }}>TERMS & CONDITIONS</h4>
        <hr />
        <div style={{ borderColor: isFormSubmitted && !formData.termsAccepted ? '#dc3545' : undefined, borderWidth: isFormSubmitted && !formData.termsAccepted ? '1px' : '0px', borderStyle: 'solid', padding: '8px', borderRadius: '4px' }}>
          <label style={{ marginBottom: '0' }}>
            <input 
              ref={fieldRefs.termsAccepted}
              type="checkbox" 
              name="termsAccepted" 
              onChange={handleChange} 
              style={{ width: '20px', marginBottom: '0' }} 
              className='me-2' 
            />
            I Accept the <a href="#">Terms & Conditions</a>*
          </label>
          {isFormSubmitted && !formData.termsAccepted && (
            <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>You must accept the Terms & Conditions</div>
          )}
        </div>
        <hr />
      </div>

      {/* Order Summary Section */}
      <div className="form-section">
        <br />
        <h4 className="text-uppercase" style={{ margin: 0 }}>ORDER SUMMARY</h4>
        <hr />
        <table className="table table-borderless">
          <tbody>
            <tr>
              <th>Provider</th>
              <td>FTTHSA</td>
            </tr>
            <tr>
              <th>Download Speed</th>
              <td>{packageDetails.speed}</td>
            </tr>
            <tr>
              <th>Upload Speed</th>
              <td>{packageDetails.speed}</td>
            </tr>
            <tr>
              <th>Router</th>
              <td>FREE</td>
            </tr>
            <tr>
              <th>Once-Off Activation Fee</th>
              <td>{packageDetails.activationFee}</td>
            </tr>
            <tr>
              <th>Monthly Payment</th>
              <td>{packageDetails.monthlyPayment}</td>
            </tr>
            <tr>
              <td colSpan={2}>
                <hr />
              </td>
            </tr>
            <tr style={{ fontSize: "1.3rem" }}>
              <th>Total Payment</th>
              <td style={{ fontWeight: "bold" }}>{packageDetails.totalPayment}</td>
            </tr>
          </tbody>
        </table>
        <hr />
      </div>

      {/* Payment Method Section */}
      <div className="form-section">
        <br />
        <h4 style={{ margin: 0 }}>CHOOSE PAYMENT METHOD</h4>
        <hr />
        <div className="d-flex flex-column"> {/* Vertical layout */}
          <label className="d-flex align-items-center mb-2"> {/* Button on left, label on right */}
            <input
              type="radio"
              name="preferred_payment_method"
              value="creditcard"
              checked={formData.preferred_payment_method === "creditcard"}
              onChange={handleChange}
              className="me-2" /* Adds small spacing between button and label */
              style={{ width: '20px', marginBottom: '0' }}
            />
            Debit / Credit Card
          </label>
          <label className="d-flex align-items-center mb-2">
            <input
              type="radio"
              name="preferred_payment_method"
              value="cash"
              checked={formData.preferred_payment_method === "cash"}
              onChange={handleChange}
              className="me-2"
              style={{ width: '20px', marginBottom: '0' }}
            />
            Cash (Mukuru)
          </label>
          <label className="d-flex align-items-center mb-2">
            <input
              type="radio"
              name="preferred_payment_method"
              value="eft"
              checked={formData.preferred_payment_method === "eft"}
              onChange={handleChange}
              className="me-2"
              style={{ width: '20px', marginBottom: '0' }}
            />
            EFT (Bank Transfer)
          </label>
          {/* <label className="d-flex align-items-center">
            <input
              type="radio"
              name="preferred_payment_method"
              value="capitecpay"
              checked={formData.preferred_payment_method === "capitecpay"}
              onChange={handleChange}
              className="me-2"
              style={{ width: '20px', marginBottom: '0' }}
            />
            Capitec Pay
          </label> */}
        </div>
        <hr />
        <div className="text-left mt-3">
        <img 
          src="/ftthsa-payment-methods.webp" // Replace with the actual path to your first image
          alt="Payment Method Illustration" 
          style={{ maxWidth: '80%', height: 'auto' }} // Adjust width to fit inline
        />
        <img 
          src="/Capitec-Pay-colour.png.webp" // Replace with the actual path to your second image
          alt="Capitec Pay Illustration" 
          style={{ maxWidth: '8%', height: 'auto' }} // Adjust width to fit inline
        />
        </div>
      </div>

      {/* Submit Buttons */}
      <div className="form-section d-flex justify-content-between">
        {/* Order Now Button */}
  <button type="submit" className="btn" onClick={handleOrderNowClick}>Order Now</button>
  <button type="submit" className="btn" onClick={handlePayLaterClick}>Pay Later</button>
      </div>

    </form>
    <br/>
    <br/>
    <br/>
    <br/>

    {/* Error Modal: pops up whenever a field fails validation, listing exactly which field(s) are at fault */}
    {errorModal.show && (
      <div
        className="error-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="error-modal-title"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050,
        }}
        onClick={closeErrorModal}
      >
        <div
          className="error-modal-content"
          style={{
            backgroundColor: '#fff',
            borderRadius: '10px',
            overflow: 'hidden',
            maxWidth: '480px',
            width: '90%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Orange header bar, matching the User Confirmation modal */}
          <div
            style={{
              backgroundColor: '#e56521',
              padding: '18px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h5 id="error-modal-title" style={{ margin: 0, color: '#fff', fontWeight: 600 }}>
              {errorModal.title}
            </h5>
            <button
              type="button"
              onClick={closeErrorModal}
              aria-label="Close"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.4rem',
                lineHeight: 1,
                cursor: 'pointer',
                color: '#fff',
              }}
            >
              &times;
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '24px', overflowY: 'auto' }}>
            <p style={{ margin: '0 0 12px 0', color: '#333' }}>
              Please fix the following {errorModal.errors.length > 1 ? 'fields' : 'field'} before continuing:
            </p>

            {errorModal.errors.length > 1 ? (
              <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
                {errorModal.errors.map((err, idx) => (
                  <li key={idx} style={{ color: '#333', marginBottom: '8px' }}>{err}</li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#333', margin: 0, fontWeight: 500 }}>{errorModal.errors[0]}</p>
            )}

            <button
              type="button"
              onClick={closeErrorModal}
              style={{
                marginTop: '20px',
                width: '100%',
                backgroundColor: '#e56521',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}

{isModalVisible && confirmationDetails && (
<UserConfirmation
  confirmationDetails={confirmationDetails}
  formData={formData}
  packageDetails={packageDetails}
  setConfirmationDetails={setConfirmationDetails}
  handleClose={() => setIsModalVisible(false)}
  onConfirm={async (updatedDetails: any) => {
    const subscriptionData = {
      portal_end_customer_id: updatedDetails.portal_end_customer_id,
      portal_product_id: packageDetails.id,
      preferred_payment_method: formData.preferred_payment_method,
      billing_auto_renew: formData.billing_auto_renew,
      application_type: formData.application_type,
      subscription_initialisation_type: formData.subscription_initialisation_type,
      total_cost: packageDetails.totalPayment,
      activation_fee: packageDetails.activationFee,
      monthly_cost: packageDetails.monthlyPayment,
      duration_days: packageDetails.durationDays,
      speed: packageDetails.speed,
      product_name: packageDetails.name,
    };

    try {
      if (payLaterRef.current) {
        await createSubscriptionPayLater(subscriptionData);
        window.location.href = '/pay-later';
      } else {
        const subscription = await createSubscription(subscriptionData);
        if (subscription.paymentRedirect) {
          window.location.href = subscription.paymentRedirect;
        }
      }
    } catch (err: any) {
      console.error('Error finalizing subscription:', err);
      // Shows exactly which field(s) failed server-side validation
      showApiError(err);
    }
  }}
/>

)}



    
    </>


  );
};

export default ApplicationForm;